import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Drawer, TextField } from "@outpave/ds";
import { useMutation } from "@apollo/client";
import moment from "moment";
import {
  DEBOUNCE_TIMEOUT,
  DEFAULT_PAGINATION_SIZE,
} from "components/common/Constants";
import useDebouncedAutoSave from "hooks/useDebouncedAutoSave";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useReceiptDetail } from "hooks/useReceiptDetail";
import { GET_TRANSACTION_ROWS } from "graphql/transaction";
import { CONNECT_RECEIPT } from "graphql/receipts";
import TransactionStatus from "enums/TransactionStatus";
import TransactionsType from "enums/TransactionsType";
import TransactionResultRow from "./TransactionResultRow";
import { toast } from "react-toastify";
import CustomSkeleton from "components/common/CustomSkeleton";
import ReceiptConnectedSuccess from "./ReceiptConnectedSuccess";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import EmployeeSearchAutoComplete, {
  Employee,
} from "components/common/EmployeeSearchAutoComplete";
import { uniqueEmployeesFromTransactionUsers } from "utils/transactions/uniqueEmployeesFromTransactions";
import { useAdminTools } from "hooks/useAdminTools";
import UserAvatar from "components/common/users/UserAvatar";

const AMOUNT_TOLERANCE = 0.1; // ±10%

// Stable references, so the variables only change when a real filter does.
// A reimbursement can take its receipt before it is paid out.
const TRANSACTION_STATUSES = [
  TransactionStatus.PENDING,
  TransactionStatus.COMPLETE,
  TransactionStatus.REFUNDED,
  TransactionStatus.PENDING_BANK_ACCOUNT,
  TransactionStatus.PENDING_PAYMENT,
  TransactionStatus.PROCESSING_PAYMENT,
  TransactionStatus.PAYMENT_FAIL,
  TransactionStatus.INSUFFICIENT_FUNDS,
];
const TRANSACTION_TYPES = [
  TransactionsType.AUTHORIZATION,
  TransactionsType.CAPTURE,
  TransactionsType.REFUND,
  TransactionsType.CASH,
];
// Not a violation filter: any transaction still without a matched receipt, required or not.
const CONNECTABLE_COMPLIANCES = ["NO_MATCHED_RECEIPT"];

const getUserDisplayName = (
  user:
    | {
        firstName?: string | null;
        lastName?: string | null;
      }
    | null
    | undefined,
  t: TFunction,
) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
  t("Unknown user");

export type TransactionNode = {
  id: string;
  user?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    profilePicUrl?: string | null;
  } | null;
  merchant?: string | null;
  merchantData?: {
    name?: string | null;
    url?: string | null;
    logo?: string | null;
    cachedLogo?: string | null;
  } | null;
  transactionDate?: number | null;
  amount?: number | null;
  currency?: string | null;
  policyStatus?: unknown;
  policyStatusDisplay?: string | null;
};

type ConnectableReceipt = {
  companyName?: string | null;
  amount?: number | null;
  transactionDate?: number | null;
};

export const connectTransactionVariables = (
  receipt: ConnectableReceipt,
  searchTerm: string,
  { mine, groupByUser }: { mine: boolean; groupByUser: boolean },
) => {
  const date = receipt.transactionDate ? moment(receipt.transactionDate) : null;
  return {
    first: DEFAULT_PAGINATION_SIZE,
    // A company-wide search starts from the receipt's merchant; your own transactions are few enough to list.
    searchTerm:
      searchTerm || (mine ? undefined : receipt.companyName) || undefined,
    // Manual selection can connect a receipt to a charge or a refund, regardless of its printed sign.
    amount: receipt.amount
      ? {
          greater: Math.abs(receipt.amount) * (1 + AMOUNT_TOLERANCE),
          less: Math.abs(receipt.amount) * (1 - AMOUNT_TOLERANCE),
          absolute: true,
        }
      : undefined,
    // The month before through the month after, to absorb posting delays.
    startDate: date?.clone().subtract(1, "month").startOf("month").valueOf(),
    endDate: date?.clone().add(1, "month").endOf("month").valueOf(),
    transactionStatus: TRANSACTION_STATUSES,
    type: TRANSACTION_TYPES,
    compliances: CONNECTABLE_COMPLIANCES,
    ...(mine && { mine: true }),
    withAdminColumns: groupByUser,
  };
};

const groupTransactionsByUser = (
  transactions: TransactionNode[],
  t: TFunction,
): {
  userId: string;
  user: TransactionNode["user"];
  transactions: TransactionNode[];
}[] => {
  const order: string[] = [];
  const map = new Map<
    string,
    {
      userId: string;
      user: TransactionNode["user"];
      transactions: TransactionNode[];
    }
  >();

  for (const node of transactions) {
    const user = node.user;
    const userId = user?.id ?? "__unknown__";
    if (!map.has(userId)) {
      map.set(userId, { userId, user, transactions: [] });
      order.push(userId);
    }
    map.get(userId)!.transactions.push(node);
  }

  return order
    .map((id) => map.get(id)!)
    .sort((a, b) =>
      getUserDisplayName(a.user, t).localeCompare(
        getUserDisplayName(b.user, t),
      ),
    );
};

interface ReceiptConnectDrawerProps {
  open: boolean;
  onClose: () => void;
  receiptId: string;
  /** Fired once the receipt is connected and the success screen is dismissed. */
  onConnected: () => void;
  mine?: boolean;
}

const ReceiptConnectDrawer: React.FC<ReceiptConnectDrawerProps> = ({
  open,
  onClose,
  receiptId,
  onConnected,
  mine = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [connected, setConnected] = useState(false);
  const [connectedTransaction, setConnectedTransaction] =
    useState<TransactionNode | null>(null);

  const { receiptDetail } = useReceiptDetail(receiptId);
  const groupByUser = useAdminTools();

  const [connectReceipt, { loading: connecting }] = useMutation(
    CONNECT_RECEIPT,
    {
      onCompleted: () => {
        setConnected(true);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  );

  const debouncedOnSearch = useDebouncedAutoSave((term: string) => {
    setSearchTerm(term);
  }, DEBOUNCE_TIMEOUT);

  useEffect(() => {
    debouncedOnSearch(inputValue);
  }, [inputValue, debouncedOnSearch]);

  // Deferred to `onAfterClose` so the panel cannot blank out while it is still
  // sliding away.
  const resetState = () => {
    setConnected(false);
    setConnectedTransaction(null);
    setInputValue("");
    setSearchTerm("");
    setSelectedEmployee(null);
  };

  // The connect cannot be cancelled mid-flight, and once it lands every way out behaves like Close.
  const handleDrawerClose = () => {
    if (connecting) return;
    if (connected) onConnected();
    else onClose();
  };

  const initialVariables = useMemo(
    () =>
      connectTransactionVariables(
        {
          companyName: receiptDetail?.companyName,
          amount: receiptDetail?.amount,
          transactionDate: receiptDetail?.transactionDate,
        },
        searchTerm,
        { mine, groupByUser },
      ),
    [
      receiptDetail?.companyName,
      receiptDetail?.amount,
      receiptDetail?.transactionDate,
      searchTerm,
      mine,
      groupByUser,
    ],
  );

  const transactionsSkipped = !open || !receiptDetail;

  const { dataArray, displayLoading, isInfiniteScrollFetching, handleScroll } =
    useInfiniteScroll({
      query: GET_TRANSACTION_ROWS,
      initialVariables,
      skip: transactionsSkipped,
      extractData: (data) =>
        data?.transactions?.edges
          ?.map((e: { node: TransactionNode | null } | null) => e?.node)
          .filter(
            (n: TransactionNode | null | undefined): n is TransactionNode =>
              Boolean(n),
          ) ?? [],
    });

  const transactions = dataArray as TransactionNode[];
  const showTransactionsLoading = transactionsSkipped || displayLoading;

  const employeesInTransactions = useMemo(
    () =>
      groupByUser
        ? uniqueEmployeesFromTransactionUsers(
            transactions.map((node) => node.user ?? null),
          )
        : [],
    [transactions, groupByUser],
  );

  useEffect(() => {
    if (!groupByUser || !selectedEmployee) return;
    if (
      employeesInTransactions.length > 0 &&
      !employeesInTransactions.some((e) => e.id === selectedEmployee.id)
    ) {
      setSelectedEmployee(null);
    }
  }, [employeesInTransactions, selectedEmployee, groupByUser]);

  const transactionsFilteredByEmployee = useMemo(() => {
    if (!groupByUser || !selectedEmployee) return transactions;
    return transactions.filter((node) => node.user?.id === selectedEmployee.id);
  }, [transactions, selectedEmployee, groupByUser]);

  const transactionsByUser = useMemo(
    () => groupTransactionsByUser(transactionsFilteredByEmployee, t),
    [transactionsFilteredByEmployee, t],
  );

  const handleConnect = (node: TransactionNode) => {
    setConnectedTransaction(node);
    connectReceipt({
      variables: {
        receiptId,
        transactionId: node.id,
      },
    });
  };

  return (
    <Drawer
      open={open}
      onClose={handleDrawerClose}
      onAfterClose={resetState}
      title={t("Connect to transaction")}
      // Layered over the full-screen Receipt Details Dialog, so lift it above
      // that modal's backdrop.
      sx={{ zIndex: theme.zIndex.modal + 2 }}
      loading={connecting}
      footer={
        connected && connectedTransaction ? (
          <Button variant="primary-filled" fullWidth onClick={onConnected}>
            {t("Close")}
          </Button>
        ) : (
          <Button
            variant="primary-outlined"
            onClick={onClose}
            disabled={connecting}
          >
            {t("Cancel")}
          </Button>
        )
      }
    >
      {connected && connectedTransaction ? (
        <ReceiptConnectedSuccess connectedTransaction={connectedTransaction} />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            gap: "var(--space-5)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
            <Typography variant="h6">
              {t("Let's connect your receipt!")}
            </Typography>
            <Typography variant="body1">
              {t("Select a transaction to connect your receipt to")}
            </Typography>
          </Box>
          {groupByUser ? (
            <EmployeeSearchAutoComplete
              title={t("All users")}
              employee={selectedEmployee}
              handleSelectEmployee={setSelectedEmployee}
              optionsFromTransactions={employeesInTransactions}
            />
          ) : null}
          <Box sx={{ flexShrink: 0 }}>
            <TextField
              search
              fullWidth
              placeholder={t("Search")}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onClear={() => setInputValue("")}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
              flex: 1,
              minHeight: 0,
            }}
          >
            <Typography variant="h6">{t("Available transactions")}</Typography>
            {showTransactionsLoading ? (
              <CustomSkeleton variant="receipt-transaction" rows={6} />
            ) : transactions.length === 0 ? (
              <Typography
                variant="body2"
                component="p"
                sx={{
                  paddingY: "var(--space-5)",
                  color: "var(--color-text-secondary)",
                }}
              >
                {t("No transactions found")}
              </Typography>
            ) : (
              <Box
                onScroll={handleScroll}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  // Room for the scrollbar without shifting rows.
                  scrollbarGutter: "stable",
                }}
              >
                {groupByUser
                  ? transactionsByUser.map((group, groupIndex) => (
                      <Box
                        key={group.userId}
                        sx={{ display: "flex", flexDirection: "column" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-3)",
                            paddingY: "var(--space-3)",
                            color: "var(--color-text-secondary)",
                          }}
                        >
                          <UserAvatar size="sm" user={group.user} />
                          <Typography variant="body2" color="textSecondary">
                            {getUserDisplayName(group.user, t)}
                          </Typography>
                        </Box>
                        {group.transactions.map((node, transactionIndex) => {
                          const isLastInGroup =
                            transactionIndex === group.transactions.length - 1;
                          const isLastGroup =
                            groupIndex === transactionsByUser.length - 1;
                          const showDividerAfter =
                            !isLastInGroup || !isLastGroup;

                          return (
                            <React.Fragment key={node.id}>
                              <TransactionResultRow
                                transaction={node}
                                onClick={() => handleConnect(node)}
                                disabled={connecting}
                              />
                              {showDividerAfter ? <Divider /> : null}
                            </React.Fragment>
                          );
                        })}
                      </Box>
                    ))
                  : transactions.map((node, index) => (
                      <React.Fragment key={node.id}>
                        <TransactionResultRow
                          transaction={node}
                          onClick={() => handleConnect(node)}
                          disabled={connecting}
                        />
                        {index < transactions.length - 1 ? <Divider /> : null}
                      </React.Fragment>
                    ))}
                {isInfiniteScrollFetching && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      paddingY: "var(--space-4)",
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default ReceiptConnectDrawer;
